import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { GbGrecaptchaComponent } from 'libs/shared/ui/src/lib/gb-grecaptcha/gb-grecaptcha.component'
import { BehaviorSubject, Observable, map } from 'rxjs'
import { ForgotPasswordConstants } from 'libs/gb-dashboard/account/feature-auth/gb-forgot-password/src/lib/forgot-password.constants'
import { environment } from 'apps/gb-dashboard/src/environments/environment'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { AccountFacade, LoginPayload } from '@galabid/gb-dashboard/account/data-access'
import * as AccountActions from 'libs/gb-dashboard/account/data-access/src/lib/+state/account.actions'
import { LoginConstants } from '../login.constants'
import { AuctionsConstants } from 'libs/gb-dashboard/auctions/feature/src/lib/auctions.constants'
import { RegisterConstants } from 'libs/gb-dashboard/account/feature-auth/register/src/lib/register.constants'
import { GeolocationFacade } from 'libs/gb-dashboard/shared/data-access-geolocation/src/lib/+state/geolocation.facade'
import * as GeolocationActions from 'libs/gb-dashboard/shared/data-access-geolocation/src/lib/+state/geolocation.actions'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'

@Component({
  selector: 'gb-login',
  templateUrl: './gb-login.component.html',
  styleUrls: ['./gb-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class GbLoginComponent implements OnInit {
  email = new FormControl('', [Validators.required, Validators.email])
  password = new FormControl('', [Validators.required])
  distributor = new FormControl('')
  loginForm: FormGroup = new FormGroup({
    email: this.email,
    password: this.password,
    distributor: this.distributor,
  })

  mfaCode = new FormControl(null, [Validators.required])
  mfaForm: FormGroup = new FormGroup({
    mfaCode: this.mfaCode,
  })

  @ViewChild(GbGrecaptchaComponent)
  recaptcha?: GbGrecaptchaComponent

  recaptchaSiteKey = environment.recaptchaSiteKey
  recaptchaSize = environment.recaptchaSize
  grecaptchaToken = ''

  showMfaForm$ = new BehaviorSubject<boolean>(false)
  isSaasLocation$ = this.geolocationFacade.isSaasLocation$

  shouldShowDistributorInput$: Observable<boolean> = this.activatedRoute.url.pipe(
    map((segments) => segments.some((segment) => segment.path === LoginConstants.DISTRIBUTOR)),
  )

  protected readonly ForgotPasswordConstants = ForgotPasswordConstants
  protected readonly RegisterConstants = RegisterConstants

  constructor(
    private accountFacade: AccountFacade,
    private geolocationFacade: GeolocationFacade,
    private activatedRoute: ActivatedRoute,
    private notificationService: GbNotificationService,
    private router: Router,
    @Inject(APP_CONFIG) protected appConfig: EnvironmentModel,
  ) {}

  ngOnInit(): void {
    this.geolocationFacade.dispatch(GeolocationActions.loadGeoLocation())
    this.accountFacade.dispatch(AccountActions.clearAccount())

    this.shouldShowDistributorInput$.subscribe((shouldShow) => {
      if (!shouldShow) {
        this.distributor.clearValidators()
        this.distributor.updateValueAndValidity()
      } else {
        this.distributor.setValidators([Validators.required])
        this.distributor.updateValueAndValidity()
      }
    })
  }

  createLoginPayload(): LoginPayload {
    const payload: LoginPayload = {
      email: this.email.value ?? '',
      password: this.password.value ?? '',
      grecaptcha_token: this.grecaptchaToken,
      distributor: this.distributor.value ?? '',
    }

    return payload
  }

  dispatchLogin() {
    this.accountFacade.dispatch(
      AccountActions.login({
        ...this.createLoginPayload(),
        onSuccess: (showMfa) => this.onSuccess(showMfa),
      }),
    )
    this.recaptcha?.reset()
  }

  onSuccess(showMfa: boolean) {
    this.showMfaForm$.next(showMfa)

    if (!showMfa) {
      this.redirectUrl()
    }
  }

  redirectUrl() {
    const redirectUrl = this.activatedRoute.snapshot.queryParamMap.get('redirectUrl')

    if (redirectUrl) {
      this.router.navigate([redirectUrl])
    } else {
      this.router.navigate([AuctionsConstants.AUCTION])
    }
  }

  dispatchMfa() {
    this.accountFacade.dispatch(
      AccountActions.login({
        ...this.createLoginPayload(),
        mfa_code: this.mfaCode.value ?? '',
        onSuccess: (showMfa) => this.onSuccess(showMfa),
      }),
    )
  }

  submitLoginForm() {
    if (this.loginForm.valid) {
      if (environment.recaptchaSize === 'invisible') {
        this.recaptcha?.execute()
      } else {
        if (!this.grecaptchaToken) {
          this.notificationService.error('RECAPTCHA_ERROR_MESSAGE')
          return
        }
        this.dispatchLogin()
      }
    }
  }

  grecaptchaCallback(grecaptchaToken: string): void {
    this.grecaptchaToken = grecaptchaToken
    if (environment.recaptchaSize === 'invisible') {
      this.dispatchLogin()
    }
  }
}
